// Lead attribution capture (frontend).
//
// Runs on every /public funnel page load. Pulls UTM params + gclid/fbclid
// from the current URL, plus document.referrer + userAgent, and:
//   1) Persists the FIRST touch to localStorage the very first time we see
//      this visitor — so if they come back a week later and submit, we still
//      credit the original ad channel.
//   2) Overwrites the LAST touch every visit — so we always credit the ad
//      that closed them, not just the one that started them.
//
// The submit flow calls getFirstAndLastTouch() to attach both touches to the
// lead payload. The backend resolves the canonical network + network_group
// (Google / Meta / Direct / …) so a mixed base of tenants stays comparable.

const FIRST_KEY = "wsq_attr_first";
const LAST_KEY = "wsq_attr_last";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const CLICK_KEYS = ["gclid", "fbclid", "msclkid", "ttclid"];

function safeStorage() {
  try {
    const s = window.localStorage;
    // probe (Safari private-mode raises on setItem)
    s.setItem("__wsq_probe__", "1"); s.removeItem("__wsq_probe__");
    return s;
  } catch { return null; }
}

function hostOf(url) {
  if (!url) return "";
  try { return new URL(url).hostname.toLowerCase(); } catch { return ""; }
}

/** Read the current window.location + document.referrer + navigator.userAgent
 * and return a raw touch object suitable for POSTing to the backend. Backend
 * enriches (network, network_group) from these fields — do NOT compute those
 * client-side to keep normalization logic in one place. */
export function captureCurrentTouch() {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const p = url.searchParams;
  const touch = {
    landing_path: url.pathname + url.search,
    referrer: document.referrer || "",
    referrer_host: hostOf(document.referrer),
    user_agent: navigator.userAgent || "",
    captured_at: new Date().toISOString(),
  };
  UTM_KEYS.forEach((k) => { const v = p.get(k); if (v) touch[k] = v.slice(0, 200); });
  CLICK_KEYS.forEach((k) => { const v = p.get(k); if (v) touch[k] = v.slice(0, 500); });
  return touch;
}

/** Merge helper: if the new touch has no UTM/click signal, keep whatever was
 * saved before (so a bookmarked direct visit after a Meta ad click doesn't
 * wipe out the Meta credit). */
function hasSignal(t) {
  if (!t) return false;
  return Boolean(
    t.utm_source || t.utm_medium || t.utm_campaign || t.gclid || t.fbclid || t.msclkid || t.ttclid,
  );
}

/** Call this ONCE on funnel page mount. Handles first-touch persistence +
 * last-touch refresh. Returns nothing — read touches via getFirstAndLastTouch()
 * at submit time. */
export function recordAttributionOnMount() {
  const store = safeStorage();
  const current = captureCurrentTouch();
  if (!current || !store) return;
  // FIRST touch: only set if not already stored
  const firstRaw = store.getItem(FIRST_KEY);
  if (!firstRaw) {
    try { store.setItem(FIRST_KEY, JSON.stringify(current)); } catch { /* ignore */ }
  }
  // LAST touch: prefer the current visit's signal; if the current visit has no
  // signal at all (direct hit / bookmark), only overwrite if we had nothing
  // stored before OR the previous last also had no signal.
  const prevLastRaw = store.getItem(LAST_KEY);
  let prevLast = null;
  try { prevLast = prevLastRaw ? JSON.parse(prevLastRaw) : null; } catch { /* ignore */ }
  const shouldReplace = hasSignal(current) || !hasSignal(prevLast);
  if (shouldReplace) {
    try { store.setItem(LAST_KEY, JSON.stringify(current)); } catch { /* ignore */ }
  }
}

/** Read both touches from localStorage. Returns a payload matching the
 * backend's Attribution model. If localStorage is unavailable, falls back to
 * a live-captured touch for both first + last. */
export function getFirstAndLastTouch() {
  const store = safeStorage();
  if (!store) {
    const t = captureCurrentTouch();
    return { first_touch: t, last_touch: t };
  }
  let first = null, last = null;
  try { first = JSON.parse(store.getItem(FIRST_KEY) || "null"); } catch { /* ignore */ }
  try { last = JSON.parse(store.getItem(LAST_KEY) || "null"); } catch { /* ignore */ }
  const live = captureCurrentTouch();
  return {
    first_touch: first || live,
    last_touch: last || live,
  };
}
